/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CRMBoardMoveRequest } from "../models/CRMBoardMoveRequest";
import type { CRMBoardMoveResponse } from "../models/CRMBoardMoveResponse";
import type { CRMBoardPersonValuesRequest } from "../models/CRMBoardPersonValuesRequest";
import type { CRMBoardPersonValuesResponse } from "../models/CRMBoardPersonValuesResponse";
import type { CRMVisualizationColumnCreate } from "../models/CRMVisualizationColumnCreate";
import type { CRMVisualizationColumnResponse } from "../models/CRMVisualizationColumnResponse";
import type { CRMVisualizationColumnUpdate } from "../models/CRMVisualizationColumnUpdate";
import type { CRMVisualizationCreate } from "../models/CRMVisualizationCreate";
import type { CRMVisualizationResponse } from "../models/CRMVisualizationResponse";
import type { CRMVisualizationUpdate } from "../models/CRMVisualizationUpdate";

import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPI } from "../core/OpenAPI";
import { request as __request } from "../core/request";

export class VisualizationsService {
  /**
   * Get Visualizations
   * Get all visualizations for an organization.
   * @returns CRMVisualizationResponse Successful Response
   * @throws ApiError
   */
  public static getVisualizations({
    orgId,
  }: {
    orgId: number;
  }): CancelablePromise<Array<CRMVisualizationResponse>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/visualizations/{org_id}",
      path: {
        org_id: orgId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Visualization
   * Create a new visualization.
   * @returns CRMVisualizationResponse Successful Response
   * @throws ApiError
   */
  public static createVisualization({
    requestBody,
  }: {
    requestBody: CRMVisualizationCreate;
  }): CancelablePromise<CRMVisualizationResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/visualizations/{org_id}",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Visualization
   * Get a specific visualization by ID.
   * @returns CRMVisualizationResponse Successful Response
   * @throws ApiError
   */
  public static getVisualization({
    visualizationId,
  }: {
    visualizationId: number;
  }): CancelablePromise<CRMVisualizationResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/visualizations/{visualization_id}",
      path: {
        visualization_id: visualizationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Visualization
   * Update an existing visualization.
   * @returns CRMVisualizationResponse Successful Response
   * @throws ApiError
   */
  public static updateVisualization({
    visualizationId,
    requestBody,
  }: {
    visualizationId: number;
    requestBody: CRMVisualizationUpdate;
  }): CancelablePromise<CRMVisualizationResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/visualizations/{visualization_id}",
      path: {
        visualization_id: visualizationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Visualization
   * Delete a visualization.
   * @returns any Successful Response
   * @throws ApiError
   */
  public static deleteVisualization({
    visualizationId,
  }: {
    visualizationId: number;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/visualizations/{visualization_id}",
      path: {
        visualization_id: visualizationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Get Visualization Columns
   * Get all columns for a visualization.
   * @returns CRMVisualizationColumnResponse Successful Response
   * @throws ApiError
   */
  public static getVisualizationColumns({
    visualizationId,
  }: {
    visualizationId: number;
  }): CancelablePromise<Array<CRMVisualizationColumnResponse>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/visualizations/{visualization_id}/columns",
      path: {
        visualization_id: visualizationId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Create Visualization Column
   * Create a new column in a visualization.
   * @returns CRMVisualizationColumnResponse Successful Response
   * @throws ApiError
   */
  public static createVisualizationColumn({
    visualizationId,
    requestBody,
  }: {
    visualizationId: number;
    requestBody: CRMVisualizationColumnCreate;
  }): CancelablePromise<CRMVisualizationColumnResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/visualizations/{visualization_id}/columns",
      path: {
        visualization_id: visualizationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Update Visualization Column
   * Update an existing column in a visualization.
   * @returns CRMVisualizationColumnResponse Successful Response
   * @throws ApiError
   */
  public static updateVisualizationColumn({
    visualizationId,
    columnId,
    requestBody,
  }: {
    visualizationId: number;
    columnId: number;
    requestBody: CRMVisualizationColumnUpdate;
  }): CancelablePromise<CRMVisualizationColumnResponse> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/visualizations/{visualization_id}/columns/{column_id}",
      path: {
        visualization_id: visualizationId,
        column_id: columnId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Delete Visualization Column
   * Delete a column from a visualization.
   * @returns any Successful Response
   * @throws ApiError
   */
  public static deleteVisualizationColumn({
    visualizationId,
    columnId,
  }: {
    visualizationId: number;
    columnId: number;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/visualizations/{visualization_id}/columns/{column_id}",
      path: {
        visualization_id: visualizationId,
        column_id: columnId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Move Board Card
   * A card was dropped on a column. A move never changes a built-in field, so
   * a built-in board refuses every drop, and a column whose rule needs a
   * built-in value the person doesn't have refuses it with where that value
   * is set. Otherwise, when the rule pins one value per custom field, those
   * values are written in one transaction and the person is checked against
   * the column; if they are not in it afterwards, nothing is saved. Any other
   * rule writes nothing and returns the column's custom fields so the board
   * can ask.
   * @returns CRMBoardMoveResponse Successful Response
   * @throws ApiError
   */
  public static moveBoardCard({
    visualizationId,
    requestBody,
  }: {
    visualizationId: number;
    requestBody: CRMBoardMoveRequest;
  }): CancelablePromise<CRMBoardMoveResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/visualizations/{visualization_id}/moves",
      path: {
        visualization_id: visualizationId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * Save Board Person Values
   * Save the board pop-up: new values for the fields this column uses, in one
   * transaction. Answers whether the person is now in the column.
   * @returns CRMBoardPersonValuesResponse Successful Response
   * @throws ApiError
   */
  public static saveBoardPersonValues({
    visualizationId,
    columnId,
    requestBody,
  }: {
    visualizationId: number;
    columnId: number;
    requestBody: CRMBoardPersonValuesRequest;
  }): CancelablePromise<CRMBoardPersonValuesResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/visualizations/{visualization_id}/columns/{column_id}/person_values",
      path: {
        visualization_id: visualizationId,
        column_id: columnId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
        500: `Internal Server Error`,
      },
    });
  }
}
